import { config } from "../services/runtimeConfig";

/**
 * MSAL against Entra External ID (CIAM). The b2c* config names are
 * historical -- Azure AD B2C closed to new tenants on 2025-05-01, so this
 * points at an External ID tenant instead.
 *
 * Two authorities are configured: normal sign-in, and an MFA "step-up"
 * authority used only when the API rejects a request with 401 mfa_required
 * (see api/security/session_manager.py's enforce_mfa_if_needed and
 * services/apiClient.js's response interceptor). This app never implements
 * MFA itself, it only redirects to the authority that enforces it.
 *
 * Under B2C the step-up authority was a second user flow with its own URL.
 * External ID has no equivalent -- MFA comes from a Conditional Access
 * policy on the ordinary sign-in -- so b2cMfaAuthority is configured empty
 * and the step-up falls back to the normal authority below. Passing an
 * empty string straight to loginRedirect({ authority }) is what MSAL
 * rejects at run time, so the fallback is load-bearing, not cosmetic.
 */
export const msalConfig = {
  auth: {
    clientId: config.b2cClientId,
    authority: config.b2cAuthority,
    knownAuthorities: [config.b2cKnownAuthority],
    redirectUri: config.b2cRedirectUri,
    postLogoutRedirectUri: config.b2cRedirectUri,
    /* FALSE. NOT the cause of "I have to click sign in twice" (#475) --
       I thought it was, changed it, and the double sign-in survived.
       The cause was main.jsx rendering before handleRedirectPromise had
       resolved; see the note there.
       
       With it true, MSAL processes the redirect at the redirect URI and
       then navigates the browser AGAIN to the page the sign-in started
       from. That second navigation is a fresh boot of the app, and the
       window between the two is one in which the signed-out view can
       render -- which is exactly what was reported: credentials
       accepted, returned to the sign-in button, second click walks
       straight in because the IdP session now exists.
       
       Kept anyway, on its own smaller merits: the redirect URI is the
       app root, so the extra hop navigates back to where the browser
       already is. One fewer navigation during sign-in is one fewer
       thing that can interleave badly. */
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "sessionStorage",
    /* TRUE, AND THIS IS THE ACTUAL CAUSE OF #475 -- third attempt.

       The device report finally carried the error, and it is thrown by
       RedirectClient.handleResponse in msal-browser 3.30.0:

           const currentAuthority = this.browserStorage.getCachedAuthority(state);
           if (!currentAuthority) {
               throw createBrowserAuthError(noCachedAuthorityError);
           }

       loginRedirect stores the request's authority in the TEMPORARY cache
       on the way out and handleResponse reads it back on the way in. It
       is the only part of the round trip kept in the browser rather than
       in the URL, so it is the only part that can go missing while the
       response itself arrives perfectly intact.

       BrowserCacheManager.getTemporaryCache looks in a cookie FIRST, and
       only when this flag is set:

           if (this.cacheConfig.storeAuthStateInCookie) {
               const itemCookie = this.cookieStorage.getItem(key);
               if (itemCookie) return itemCookie;
           }
           const value = this.temporaryCacheStorage.getItem(key);

       With it false there was no first branch at all, so that authority
       lived only in sessionStorage -- the storage with the weakest claim
       to surviving a navigation out to an identity provider and back.
       When it did not survive, handleRedirectPromise threw, main.jsx
       caught it and rendered the app signed out, and somebody who had
       just typed a correct password got the sign-in button back.

       WHY THE FIRST TWO ATTEMPTS DID NOT FIND THIS. Both were about
       ORDERING -- navigateToLoginRequestUrl, then rendering before the
       promise resolved -- and both were reasonable readings of a symptom
       that looks exactly like a race. Attempt 2 did fix the ordering.
       The remaining failure was never a race; the handshake could not be
       completed at all. A thrown redirect and a too-early render produce
       an identical screen, which is what kept this alive through two
       fixes.

       The cookie is short-lived, written and cleaned by MSAL itself
       alongside the state and nonce it already manages. See
       services/authRedirectRecovery.js for the belt-and-braces half. */
    storeAuthStateInCookie: true,
    /*
     * AND SENT ONLY OVER HTTPS.
     *
     * msal-browser defaults secureCookies to FALSE (see its
     * Configuration.mjs), so turning storeAuthStateInCookie on above --
     * which is what fixed #475 -- started writing auth state to cookies
     * with no Secure attribute. That is a weakness this change
     * introduced rather than one that was already here, which is why it
     * belongs next to the line that caused it.
     *
     * What rides in these cookies is the state and nonce for a sign-in
     * in flight. Not a token, so the exposure is narrow, but it is
     * exactly the material a network attacker would want to interfere
     * with a redirect -- and there is no cost to the flag: every
     * environment this app is served from is HTTPS, including dev.
     *
     * NOT the cookie Chrome warns about on the login page. That one is
     * `esctx-*` on *.ciamlogin.com, which Entra sets on its own domain;
     * nothing in this repository can change its attributes. This is the
     * half we do control.
     */
    secureCookies: true,
  },
};

export const mfaStepUpAuthority = config.b2cMfaAuthority || config.b2cAuthority;

export const loginRequest = {
  scopes: [`${config.b2cClientId}/.default`],
};
